import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser, LoginInput, RegisterInput, Role } from '@hch/shared';
import { api, ApiError } from './api';

/**
 * Session utilisateur.
 *
 * La session n'est pas conservee dans `localStorage` : elle est reconstruite au
 * demarrage par un appel a `GET /auth/me`, qui s'appuie sur le cookie `httpOnly`.
 * Consequence voulue : le front ne peut pas se croire connecte alors que le serveur
 * a revoque la session, et rien d'exploitable ne subsiste dans le navigateur.
 */

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (...roles: Role[]) => boolean;
  login: (input: LoginInput) => Promise<AuthenticatedUser>;
  register: (input: RegisterInput) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.get<AuthenticatedUser>('/auth/me');
      } catch (error) {
        // 401 au demarrage = visiteur non connecte. Ce n'est pas une erreur :
        // la majorite du parcours client est accessible sans compte (RG-04).
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const user = data ?? null;

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      hasRole: (...roles: Role[]) => !!user && roles.includes(user.role),

      login: async (input) => {
        const session = await api.post<{ user: AuthenticatedUser }>('/auth/login', input);
        queryClient.setQueryData(['auth', 'me'], session.user);
        return session.user;
      },

      register: async (input) => {
        const session = await api.post<{ user: AuthenticatedUser }>('/auth/register', input);
        queryClient.setQueryData(['auth', 'me'], session.user);
        return session.user;
      },

      logout: async () => {
        // La purge locale se fait QUOI QU'IL ARRIVE. Se deconnecter est aussi un acte
        // local : l'etat du navigateur ne peut pas dependre du reseau. Le serveur ne peut
        // pas echouer pour une session expiree — `POST /auth/logout` est `@Public()` et
        // vide les cookies dans tous les cas — mais il peut etre injoignable. Sans ce
        // `finally`, le rejet remontait a un appelant qui fait `void logout()`, partait en
        // silence, et l'interface restait connectee.
        try {
          await api.post('/auth/logout');
        } catch {
          // Volontairement avale : l'utilisateur veut etre deconnecte, pas informe
          // qu'une requete a echoue.
        } finally {
          // L'ORDRE COMPTE, et c'etait la le defaut.
          //
          // Le code precedent appelait `queryClient.clear()` PUIS reecrivait la session a
          // `null`. Or `clear()` supprime du cache l'entree ['auth','me'] a laquelle
          // l'observateur monte par `useQuery` est abonne. Le `setQueryData` suivant
          // creait donc une entree NEUVE, que plus personne n'ecoutait : les cookies
          // partaient bien, l'URL changeait, mais le composant gardait son dernier
          // resultat connu et l'interface restait affichee comme connectee jusqu'a un
          // rechargement manuel. C'est exactement le symptome constate.
          //
          // On ecrit donc la session a `null` D'ABORD — l'observateur en place recoit la
          // mise a jour et se rend — puis on purge tout le RESTE, sans quoi les
          // donnees du compte precedent resteraient visibles apres une reconnexion avec
          // un autre compte.
          queryClient.setQueryData(['auth', 'me'], null);
          queryClient.removeQueries({
            predicate: (requete) => requete.queryKey[0] !== 'auth',
          });
        }
      },

      refresh: async () => {
        await refetch();
      },
    }),
    [user, isLoading, queryClient, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit etre utilise a l’interieur d’un AuthProvider.');
  }
  return context;
}

/** Route d'accueil de chaque role, utilisee apres connexion. */
export function homeRouteFor(role: Role): string {
  switch (role) {
    case 'ADMIN':
      return '/admin';
    case 'TECHNICIAN':
      return '/technicien';
    default:
      return '/mon-compte';
  }
}
